import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { SimpleGrid } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';

import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';
import { StudentEnrolmentHealth as EnrolmentHealthEnum } from '@duke-hq/svc-api-db';

import useDashboardContext from '../../context/useDashboardContext';
import ErrorFallback from '../ErrorFallback';
import CardGroup from './shared/CardGroup';
import Section from './shared/Section';
import StatCard from './shared/StatCard';
import buildLinkToEnrolments from '../../utils/buildLinkToEnrolments';

type Props = {
  data?: RouterOutputs['dashboard']['getEnrolmentHealth'];
};

function EnrolmentHealthUI({ data }: Props) {
  const { notStarted, behind, slipping, onTrack } = data ?? {};

  return (
    <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing="8px">
      <StatCard
        backgroundColour="bg.canvas"
        title={t`Not started`}
        titleColour="fg.muted"
        description={t`No activity`}
        descriptionColour="danger.emphasis"
        value={notStarted}
        valueColour="fg.emphasis"
        to={buildLinkToEnrolments({ health: EnrolmentHealthEnum.NOT_STARTED })}
      />
      <StatCard
        backgroundColour="bg.canvas"
        title={t`Behind`}
        titleColour="fg.muted"
        description={t`>2 units behind pace`}
        descriptionColour="danger.emphasis"
        value={behind}
        valueColour="fg.emphasis"
        to={buildLinkToEnrolments({ health: EnrolmentHealthEnum.BEHIND })}
      />
      <StatCard
        backgroundColour="bg.canvas"
        title={t`Slipping`}
        titleColour="fg.muted"
        description={t`<2 units behind pace`}
        descriptionColour="yellow.500"
        value={slipping}
        valueColour="fg.emphasis"
        to={buildLinkToEnrolments({ health: EnrolmentHealthEnum.SLIPPING })}
      />
      <StatCard
        backgroundColour="bg.canvas"
        title={t`On track`}
        titleColour="fg.muted"
        description={t`<1 unit behind pace`}
        descriptionColour="success"
        value={onTrack}
        valueColour="fg.emphasis"
        to={buildLinkToEnrolments({ health: EnrolmentHealthEnum.ON_TRACK })}
      />
    </SimpleGrid>
  );
}

function EnrolmentHealthWithData() {
  const { filters } = useFiltersContext();
  const { data } = trpcTenant.dashboard.getEnrolmentHealth.useQuery(
    { filters },
    {
      suspense: true,
      trpc: {
        context: {
          skipBatch: true,
        },
      },
    },
  );

  if (!data || Object.keys(data).length === 0) {
    return <ErrorFallback />;
  }

  return <EnrolmentHealthUI data={data} />;
}

export default function EnrolmentHealth() {
  const { openPanel } = useDashboardContext();

  return (
    <Section
      title={t`Enrolment health`}
      tooltipText={t`How many units a student is behind course pace`}
    >
      <CardGroup cardType="muted" title={t`Status`} openPanel={() => openPanel('EnrolmentHealth')}>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Suspense fallback={<EnrolmentHealthUI />}>
            <EnrolmentHealthWithData />
          </Suspense>
        </ErrorBoundary>
      </CardGroup>
    </Section>
  );
}
